import React, { createContext, useCallback, useContext, useState } from 'react';

import _ from 'lodash';

const DepartmentsBudgetsReportContext = createContext();

export const useDepartmentsBudgetsReportTableContext = () => {
    const context = useContext(DepartmentsBudgetsReportContext);

    if (!context) {
        throw new Error('useDepartmentsBudgetsReportTableContext must be used within a DepartmentsBudgetsReportTableContext');
    }

    return context;
};

export const DepartmentsBudgetsReportTableProvider = ({
    criteria,
    report,
    budgets,
    comments,
    allComments,
    userType,
    loading,
    showComments = false,
    editableComments = false,
    editedComments,
    onCommentChange,
    children,
    markupCategories,
}) => {
    const [expanded, setExpanded] = useState({ revenue: {}, expense: {} });

    const toggleExpansion = useCallback(
        (type, ...keys) => {
            const [department, costcenter, project] = keys;
            const options = { ...expanded };

            if (project) {
                if (costcenter) {
                    _.set(
                        options,
                        [type, department, 'costcenters', costcenter, 'projects', project, 'expanded'],
                        !_.get(options, [type, department, 'costcenters', costcenter, 'projects', project, 'expanded'], false),
                    );
                } else {
                    _.set(
                        options,
                        [type, department, 'projects', project, 'expanded'],
                        !_.get(options, [type, department, 'projects', project, 'expanded'], false),
                    );
                }
            } else if (costcenter) {
                _.set(
                    options,
                    [type, department, 'costcenters', costcenter, 'expanded'],
                    !_.get(options, [type, department, 'costcenters', costcenter, 'expanded'], false),
                );
            } else if (department) {
                _.set(options, [type, department, 'expanded'], !_.get(options, [type, department, 'expanded'], false));
            }

            setExpanded(options);
        },
        [expanded],
    );

    const toggleAllExpansion = useCallback(
        (expand = true) => {
            if (!report) {
                return;
            }

            const options = {};

            report.revenues.groups.forEach((departmentGroup) => {
                _.set(options, ['revenue', departmentGroup.id, 'expanded'], expand);

                departmentGroup.groups.forEach((costcenter) => {
                    _.set(options, ['revenue', departmentGroup.id, 'costcenters', costcenter.id, 'expanded'], expand);
                });
            });

            report.expenses.groups.forEach((departmentGroup) => {
                _.set(options, ['expense', departmentGroup.id, 'expanded'], expand);

                departmentGroup.groups.forEach((costcenter) => {
                    _.set(options, ['expense', departmentGroup.id, 'costcenters', costcenter.id, 'expanded'], expand);

                    costcenter.subgroups.forEach((project) => {
                        _.set(options, ['expense', departmentGroup.id, 'costcenters', costcenter.id, 'projects', project.id, 'expanded'], expand);
                    });
                });

                departmentGroup.subgroups.forEach((project) => {
                    _.set(options, ['expense', departmentGroup.id, 'projects', project.id, 'expanded'], expand);
                });
            });

            setExpanded(options);
        },
        [expanded, report],
    );

    const isOptionIncluded = useCallback(
        (optionValue) => {
            return criteria && !!criteria[`include_${optionValue}`];
        },
        [criteria],
    );

    const value = {
        loading: loading,
        report: report,
        budgets: budgets,
        markupCategories: markupCategories,
        expanded,
        criteria,
        comments,
        allComments,
        userType,
        showComments,
        editableComments,
        editedComments,
        onCommentChange,
        toggleExpansion,
        toggleAllExpansion,
        isOptionIncluded,
    };

    return <DepartmentsBudgetsReportContext.Provider value={value}>{children}</DepartmentsBudgetsReportContext.Provider>;
};

export default DepartmentsBudgetsReportContext;
